import React, { useEffect, useMemo, useState } from 'react';
import driverApi from '../../clients/DriverClient';
import { type DriverRaceDataResponse } from '../../models/DriverRaceDataResponse';
import { type DriverUsedFacility } from '../../models/RaceData';
import { formatMilliseconds, formatDateOnlyLocal } from '../../utils/time';
import { formatLocation } from '../../utils/bookingUtils';
import { useAuth } from '../../context/AuthContext';
import CustomFilterDropdown from '../CustomFilterDropdown';
import EmptyState from '../EmptyState';

interface DriverLeaderboardsViewProps {
  driverId: string | undefined;
  facilities: DriverUsedFacility[];
  selectedFacility: DriverUsedFacility | null;
  isLoadingFacilities: boolean;
  facilitiesError: string;
  onFacilityChange: (facilityId: string) => void;
}

const DriverLeaderboardsView: React.FC<DriverLeaderboardsViewProps> = ({
  driverId,
  facilities,
  selectedFacility,
  isLoadingFacilities,
  facilitiesError,
  onFacilityChange
}) => {
  const { user } = useAuth();
  const [races, setRaces] = useState<DriverRaceDataResponse[]>([]);
  const [isLoadingRaces, setIsLoadingRaces] = useState(false);
  const [racesError, setRacesError] = useState('');

  const facilityOptions = useMemo(() => {
    return facilities.map(f => ({
      id: `${f.track}-${f.trackLayout}-${f.car}`,
      value: `${f.track}-${f.trackLayout}-${f.car}`,
      label: `🏁 ${f.trackFriendlyName} • 🚗 ${f.carFriendlyName}`
    }));
  }, [facilities]);

  // Load leaderboard data when selected facility changes
  useEffect(() => {
    if (selectedFacility && driverId) {
      fetchLeaderboard(selectedFacility);
    }
  }, [selectedFacility, driverId]);

  const fetchLeaderboard = async (facility: DriverUsedFacility) => {
    if (!driverId) return;
    setIsLoadingRaces(true);
    setRacesError('');
    try {
      const result = await driverApi.getDriverLeaderboard({
        take: 100, // Get more results for leaderboard
        skip: 0,
        car: facility.car,
        track: facility.track,
        trackLayout: facility.trackLayout
      });
      setRaces(result || []);
    } catch (err) {
      console.error(err);
      setRacesError('Failed to load leaderboard data');
    } finally {
      setIsLoadingRaces(false);
    }
  };

  const handleFacilityChange = (value: string) => {
    onFacilityChange(value);
  };

  return (
    <div className="card h-100">
      <div className="card-body">

        {isLoadingFacilities && (
          <div className="text-center py-4">
            <EmptyState message="Loading leaderboard data..." animate={true} />
          </div>
        )}

        {!isLoadingFacilities && facilitiesError && (
          <div className="alert alert-danger" role="alert">{facilitiesError}</div>
        )}

        {!isLoadingFacilities && !facilitiesError && facilities.length === 0 && (
          <EmptyState />
        )}

        {!isLoadingFacilities && !facilitiesError && facilities.length > 0 && (
          <div>
            <div className="mb-3">
              <CustomFilterDropdown
                options={facilityOptions}
                value={selectedFacility ? `${selectedFacility.track}-${selectedFacility.trackLayout}-${selectedFacility.car}` : ''}
                onChange={handleFacilityChange}
                ariaLabel="Select facility"
                placeholder="Select Facility"
              />
            </div>

            {selectedFacility && isLoadingRaces && (
              <div className="text-center py-4">
                <EmptyState message="Loading leaderboard data..." animate={true} />
              </div>
            )}

            {selectedFacility && !isLoadingRaces && racesError && (
              <div className="alert alert-danger" role="alert">{racesError}</div>
            )}

            {selectedFacility && !isLoadingRaces && !racesError && races.length === 0 && (
              <div className="text-center py-4">
                <p className="text-paragraphs mb-0">No leaderboard data found for this facility.</p>
              </div>
            )}

            {selectedFacility && !isLoadingRaces && !racesError && races.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-hover table-custom align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th className="text-center">Driver</th>
                      <th className="text-center">Date</th>
                      <th className="text-center">Location</th>
                      <th className="text-center hide-mobile">Sec1</th>
                      <th className="text-center hide-mobile">Sec2</th>
                      <th className="text-center hide-mobile">Sec3</th>
                      <th className="text-center">Time</th>
                      <th className="text-center hide-mobile">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {races.map((r, index) => {
                      const isFirstPlace = index === 0;
                      const isCurrentDriver = user?.driverId && r.driverId === user.driverId;
                      const isCurrentDriverNotFirst = isCurrentDriver && !isFirstPlace;

                      return (
                        <tr
                          key={(r.id ?? '') + r.date + r.track + r.layout}
                          style={isFirstPlace ? {
                            border: '3px solid #ffd700',
                            fontWeight: 'bold',
                            boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
                          } : isCurrentDriverNotFirst ? {
                            border: '3px solid var(--bs-red)',
                            fontWeight: 'bold',
                            boxShadow: '0 0 10px rgba(var(--bs-red-rgb), 0.5)'
                          } : {}}
                        >
                          <td>{r.position}</td>
                          <td className="text-center">{r.driver}</td>
                          <td className="text-center">{formatDateOnlyLocal(r.date)}</td>
                          <td className="text-center">{formatLocation(r.location)}</td>
                          <td className="text-center time-value hide-mobile">{formatMilliseconds(r.sec1)}</td>
                          <td className="text-center time-value hide-mobile">{formatMilliseconds(r.sec2)}</td>
                          <td className="text-center time-value hide-mobile">{formatMilliseconds(r.sec3)}</td>
                          <td className="text-center time-value">{formatMilliseconds(r.time)}</td>
                          <td className={`text-center hide-mobile time-value ${r.deltaFromFirst > 0 ? 'text-danger' : r.deltaFromFirst === 0 ? 'text-success' : ''}`}>
                            {formatMilliseconds(r.deltaFromFirst)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverLeaderboardsView;
